import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { invariant } from '../../util/invariant';
import type { MimirIndexEntry, MimirIndex, MimirIndexFixedFrameSize, MimirIndexVariableFrameSize } from './mimirIndexFile';
import { safeArrayAccess } from '@mimir/common/util/safeArrayAccess';

export interface SubClipIndexInfo {
  // The required subsection of the index table
  mimirIndex: MimirIndex,
  // The number of frames removed from the beginning of the
  // file.
  removedFramesCount: number,
  // Number of frames that was added to the start of the
  // index table in order to make the sub clip valid
  leadInFrameCount: number,
  // Number of frames after the leadInFrameCount that are to
  // be actually considered part of the sub clip.
  validFrameCount: number
}

export function calculateSubClipIndexInfo(
  structuredLogger: StructuredLogger,
  mimirIndex: MimirIndex,
  absoluteInPointFrameNumber: number,
  absoluteOutPointFrameNumber: number,
  fileSize: number,
): SubClipIndexInfo {
  switch (mimirIndex.type) {
    case 'fixed_frame_size':
      return calculateSubClipIndexInfoFixedFrameSize(structuredLogger, mimirIndex, absoluteInPointFrameNumber, absoluteOutPointFrameNumber, fileSize);
    case 'variable_frame_size':
      return calculateSubClipIndexInfoVariableFrameSize(mimirIndex, absoluteInPointFrameNumber, absoluteOutPointFrameNumber);
  }
}

// End of public API

function calculateSubClipIndexInfoFixedFrameSize(
  structuredLogger: StructuredLogger,
  mimirIndex: MimirIndexFixedFrameSize,
  inPointFrameNumber: number,
  outPointFrameNumber: number,
  fileSize: number,
): SubClipIndexInfo {

  const frameCount =
    Math.floor((fileSize - mimirIndex.firstFrameByteOffset) / mimirIndex.fixedFrameSize);

  const clampedOutPoint = Math.min(frameCount, outPointFrameNumber);
  const clampedInPoint = Math.min(frameCount, inPointFrameNumber);

  if (outPointFrameNumber !== clampedOutPoint) {
    structuredLogger.log({ level: 'warn', message: 'Clamped out point', outPointFrameNumber, clampedOutPoint });
  }

  return {
    mimirIndex: {
      type: 'fixed_frame_size',
      fixedFrameSize: mimirIndex.fixedFrameSize,
      firstFrameByteOffset: mimirIndex.firstFrameByteOffset + (mimirIndex.fixedFrameSize * inPointFrameNumber),
    },
    removedFramesCount: clampedInPoint,
    leadInFrameCount: 0,
    validFrameCount: clampedOutPoint - clampedInPoint,
  };
}

function calculateSubClipIndexInfoVariableFrameSize(
  mimirIndex: MimirIndexVariableFrameSize,
  inPoint: number,
  outPoint: number,
): SubClipIndexInfo {
  let entries : MimirIndexEntry[] = [...mimirIndex.entries];

  // First we just make sure that the in and out points don't exceed the limits of the
  // full index table
  inPoint = Math.max(inPoint, 0);
  outPoint = Math.min(outPoint, mimirIndex.entries.length);

  invariant(inPoint < outPoint, 'MXF subclip evaluated to zero frames');



  // Since both the actual frames and the index information for these frames are stored
  // in decode order we first need to figure out the decode order frame number of our
  // inPoint.
  //
  //                                          +-- inPoint=0x0A
  //                                          |
  //                                          V
  // Frame number (hex):  0 1 2 3 4 5 6 7 8 9 A B C D E
  // Presentation order:  B B I B B P B B I B B P B B P
  //                                          |
  //                                          +-+  (temporalOffset: +1)
  //                                            |
  //                                            V
  // Decode order:        I B B P B B I B B P B B P B B
  //                                            ^
  //                                            |
  //                                            +-- decodeOrderInPoint=0x0B
  //
  //
  const decodeOrderInPoint = inPoint + entries[inPoint]!.temporalOffset;

  // Now that we have the first frame in decode order we can look up the key frame offset
  // information in the index table to obtain the preceding I-frame that we will need to
  // include in order to be able to decode our in-point frame:
  //
  //                                            +-- decodeOrderInPoint=0x0B
  //                                            |
  //                                            V
  // Frame number (hex):  0 1 2 3 4 5 6 7 8 9 A B C D E
  // Decode order:        I B B P B B I B B P B B P B B
  //                                  ^         |
  //               adjustedInPoint=6  |         |
  //                                  +---------+  keyFrameOffset: -5
  //
  const adjustedInPoint = decodeOrderInPoint + entries[decodeOrderInPoint]!.keyFrameOffset;

  // We also may need to adjust the out point. Just like inPoint the outPoint is specified
  // in presentation order. This means that given the following outPoint there could be
  // gaps in the decode order which is not possible to express using the MXF index tables.
  // Because of this, we need to shift the out point forward until there is no gaps in
  // the decode order and set the "Duration" correctly in order to indicate how many of
  // the trailing frames to ignore.
  //
  //                                        +----- last frame to be included=0x09
  //                                        | +-- outPoint=0x0A
  //                                        | |
  //                                        V V
  // Frame number (hex):  0 1 2 3 4 5 6 7 8 9 A B C D E
  // Presentation order:  B B I B B P B B I B - - - - -
  //                                        |
  //                                        +-+  (temporalOffset: +1)
  //                                          |
  //                                          V
  // Decode order:        I B B P B B I B B - B - - - -
  //                                          ^
  //                                          |
  //                                          +-- decodeOrderInPoint=0x0B
  //
  // In the example above we can see that the P frame needed to decode the
  // final B-frame is not included.

  let adjustedOutPoint = outPoint;
  while (entries[adjustedOutPoint - 1]!.temporalOffset > 0) {
    if (adjustedOutPoint >= mimirIndex.entries.length) {
      break;
    }
    adjustedOutPoint += 1;
  }

  // We can now strip all the entries from the index which comes before the
  // adjusted in point:
  //
  // Frame number:        - - - - - - 0 1 2 3 4 5 6 7 8
  // Presentation order:  - - - - - - B B I B B P B B P
  // Decode order:        - - - - - - I B B P B B P B B

  entries = entries.slice(adjustedInPoint, adjustedOutPoint);

  // For video files with B-frames and open GOP enabled we must also consider the fact that
  // the first frames following the I-frame may be B-frame of the previous GOP. Ideally we
  // would strip these from the file, but this is not possible since the video packet essence
  // also holds information about temporal offset and if we start removing frames in the
  // middle of the stream there would be a disagreement between the essence and the index.
  // It seems that in order to get past the MXF validation of most application we just need
  // to make sure that the keyFrameOffset does not reference frame numbers outside the
  // index table. We cheat and lie about the keyFrameOffset for these leading B-frames so
  // that they point to the I-frame of the current GOP instead of the I-frame of the
  // previous GOP. Based on the leadInFrameCount value the reading application should ignore
  // the leading B-frames and thus not discover that the essence of these packets references
  // another key frame than the one specified by the index.

  for (let i = 0; i < entries.length; i++) {
    const temporalOffset = safeArrayAccess(entries, i)!.temporalOffset;
    // Only B-frames will have positive temporal offset
    if (temporalOffset > 0) {
      const dts = i + temporalOffset;
      // B-frames belonging to the previous GOP will now have negative
      // decode position.
      const entry = entries[dts];

      invariant(entry !== undefined, 'Decode position is outside of index range');

      if (dts + entry.keyFrameOffset < 0) {
        entry.keyFrameOffset = -dts;
      } else {
        // This condition will occur for B-frames preceding and I-frame
        // in presentation order but not referencing anything but the
        // I-frame in front of it. In these cases the B-frames following
        // the I-frame in decode order are actually part of the GOP and
        // must not be stripped.
        break;
      }
    } else {
      break;
    }
  }

  return {
    mimirIndex: {
      type: 'variable_frame_size',
      entries,
    },
    removedFramesCount: adjustedInPoint,
    leadInFrameCount: inPoint - adjustedInPoint,
    validFrameCount: outPoint - inPoint,
  };
}
